import React, { useEffect, useState, useMemo } from 'react';
import { Box, Collapse, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/EnterpriseAuthContext';
import { useApp } from '../context/AppContext';
import { getDocumentUsage, getSessionUsage } from '../services/usageService';

/** @type {string} localStorage key for collapsed state */
const METRICS_EXPANDED_STORAGE_KEY = 'quillai:home-metrics-expanded';

/**
 * Read whether the metrics panel should start expanded.
 * @returns {boolean} True if expanded (default when unset).
 */
function readStoredMetricsExpanded() {
  try {
    const stored = localStorage.getItem(METRICS_EXPANDED_STORAGE_KEY);
    if (stored === null) {
      return true;
    }
    return stored === 'true';
  } catch {
    return true;
  }
}

/**
 * Persist metrics panel expanded state.
 * @param {boolean} expanded - Whether the panel is expanded.
 * @returns {void}
 */
function persistMetricsExpanded(expanded) {
  try {
    localStorage.setItem(METRICS_EXPANDED_STORAGE_KEY, String(expanded));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Heuristic: estimated hours saved vs manual work, from monthly document and session activity.
 * @param {number} documentsThisMonth - Documents generated this month (usage_events).
 * @param {number} sessionsThisMonth - Sessions recorded this month (usage_events).
 * @returns {number} Estimated hours, one decimal place.
 */
function estimateHoursSaved(documentsThisMonth, sessionsThisMonth) {
  const hoursPerDocument = 2;
  const hoursPerSession = 0.5;
  const raw = documentsThisMonth * hoursPerDocument + sessionsThisMonth * hoursPerSession;
  return Math.round(raw * 10) / 10;
}

/**
 * One KPI tile. Renders as a link when `to` is set.
 * @param {object} props - Props.
 * @param {string} props.label - Metric label.
 * @param {React.ReactNode} props.value - Metric value.
 * @param {string} [props.hint] - Tooltip / accessible description.
 * @param {string} [props.to] - Route to open.
 * @param {boolean} [props.loading] - Show a skeleton value.
 * @returns {JSX.Element} Tile.
 */
function KpiTile({ label, value, hint, to, loading = false }) {
  const linkProps = to ? { component: RouterLink, to } : {};
  return (
    <Box
      {...linkProps}
      title={hint}
      sx={{
        bgcolor: 'background.paper',
        // Hairlines between tiles; the container clips the ones on its outer edges.
        boxShadow: '1px 0 0 0 var(--q-border), 0 1px 0 0 var(--q-border)',
        px: 2.5,
        py: 2,
        textDecoration: 'none',
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        minWidth: 0,
        transition: 'background-color 120ms ease',
        ...(to ? {
          '&:hover': { bgcolor: 'var(--q-bg-subtle)' },
          '&:focus-visible': { outline: '2px solid var(--q-focus-ring)', outlineOffset: -2 },
        } : {}),
      }}
    >
      <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>{label}</Typography>
      {loading ? (
        <Skeleton variant="text" width={56} height={36} />
      ) : (
        <Typography component="span" sx={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Workspace KPI strip for the home dashboard: customers, projects, templates, documents
 * generated this month and an hours-saved estimate. Monthly usage comes from Supabase via the
 * usage helpers; entity counts come from AppContext. Collapsible (preference kept per browser).
 * @param {object} [props] - Props.
 * @param {boolean} [props.showTemplates=true] - Include the templates tile.
 * @param {boolean} [props.collapsible=true] - Offer the hide / show toggle (its preference is
 *   per browser). Admin > Overview holds only these tiles, so it shows them always expanded.
 * @returns {JSX.Element|null} Metrics section or null when not authenticated.
 */
function HomeMetrics({ showTemplates = true, collapsible = true } = {}) {
  const { state: authState } = useAuth();
  const { state: appState } = useApp();
  const [storedExpanded, setMetricsExpanded] = useState(readStoredMetricsExpanded);
  const metricsExpanded = collapsible ? storedExpanded : true;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentsThisMonth, setDocumentsThisMonth] = useState(0);
  const [sessionsThisMonth, setSessionsThisMonth] = useState(0);

  const activeProjectCount = appState.projects?.length ?? 0;
  const customerCount = appState.customers?.length ?? 0;
  const templateCount = appState.templates?.length ?? 0;
  const entitiesLoading = !appState.initialLoadComplete;

  const hoursSaved = useMemo(
    () => estimateHoursSaved(documentsThisMonth, sessionsThisMonth),
    [documentsThisMonth, sessionsThisMonth]
  );

  useEffect(() => {
    const accountId = authState.activeAccount?.id;
    const userId = authState.user?.id;
    if (!accountId || !userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [docs, sessions] = await Promise.all([
          getDocumentUsage(userId, accountId),
          getSessionUsage(userId, accountId),
        ]);
        if (!cancelled) {
          setDocumentsThisMonth(docs);
          setSessionsThisMonth(sessions);
        }
      } catch (err) {
        console.error('HomeMetrics: failed to load usage', err);
        if (!cancelled) {
          setError(err.message || 'Could not load metrics.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [authState.activeAccount?.id, authState.user?.id]);

  if (!authState.activeAccount?.id || !authState.user?.id) {
    return null;
  }

  /**
   * Toggle metrics panel visibility and persist preference.
   * @returns {void}
   */
  const handleToggleMetricsExpanded = () => {
    setMetricsExpanded((prev) => {
      const next = !prev;
      persistMetricsExpanded(next);
      return next;
    });
  };

  const tiles = [
    { key: 'customers', label: 'Customers', value: customerCount, to: '/customers', loading: entitiesLoading },
    { key: 'projects', label: 'Projects', value: activeProjectCount, to: '/projects', loading: entitiesLoading, hint: 'Projects you own or that are shared with you' },
    showTemplates ? { key: 'templates', label: 'Templates', value: templateCount, to: '/templates', loading: entitiesLoading } : null,
    { key: 'documents', label: 'Documents this month', value: documentsThisMonth, to: '/documents', loading, hint: 'AI-generated deliverables this calendar month' },
    { key: 'hours', label: 'Hours saved (est.)', value: hoursSaved, loading, hint: 'Estimated from documents generated and sessions recorded this month' },
  ].filter(Boolean);

  return (
    <Box component="section" aria-labelledby="home-metrics-heading">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: metricsExpanded ? 1 : 0 }}>
        <Typography id="home-metrics-heading" component="h2" sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
          At a glance
        </Typography>
        {collapsible && (
          <Tooltip title={metricsExpanded ? 'Hide metrics' : 'Show metrics'}>
            <IconButton
              size="small"
              onClick={handleToggleMetricsExpanded}
              aria-expanded={metricsExpanded}
              aria-controls="home-metrics-panel"
              aria-label={metricsExpanded ? 'Hide metrics' : 'Show metrics'}
              sx={{ p: 0.25 }}
            >
              <ExpandMore
                fontSize="small"
                sx={{
                  transform: metricsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: (theme) => theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {error && metricsExpanded && (
          <Typography variant="caption" sx={{ color: 'var(--q-warning-text)' }} role="status">
            Monthly usage is unavailable right now.
          </Typography>
        )}
      </Box>

      <Collapse in={metricsExpanded}>
        <Box
          id="home-metrics-panel"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'var(--q-radius-card)',
            overflow: 'hidden',
          }}
        >
          {tiles.map((tile) => (
            <KpiTile key={tile.key} label={tile.label} value={tile.value} to={tile.to} hint={tile.hint} loading={tile.loading} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default HomeMetrics;
